/**
 * Fallback connection parameter sets.
 *
 * Static, connector-keyed parameter-set definitions used by
 * ConnectionCreationPanel when a live connector manifest is not available
 * (e.g. offline, or the metadata fetch returned nothing). Pure data — no logic.
 * Lifted verbatim from ConnectionCreationPanel to slim the component.
 */
import type { ConnectionParameterSet } from '@/types/connectionParameters';

export const FALLBACK_PARAMETER_SETS: Record<string, ConnectionParameterSet[]> = {
  // Agent connections - used for Agent operations
  // The actual form depends on agentModelType parameter but we provide common auth methods
  Agent: [
    {
      name: 'AzureOpenAI_ManagedIdentity',
      uiDefinition: {
        displayName: 'Azure OpenAI (Managed Identity)',
        description: 'Connect to Azure OpenAI using Managed Identity authentication',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            tooltip: 'e.g., https://your-resource.openai.azure.com',
            constraints: { required: 'true' },
          },
        },
      },
    },
    {
      name: 'AzureOpenAI_ApiKey',
      uiDefinition: {
        displayName: 'Azure OpenAI (API Key)',
        description: 'Connect to Azure OpenAI using an API key',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            tooltip: 'e.g., https://your-resource.openai.azure.com',
            constraints: { required: 'true' },
          },
        },
        openAIKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your Azure OpenAI API key',
            constraints: { required: 'true' },
          },
        },
      },
    },
    {
      name: 'V1ChatCompletions',
      uiDefinition: {
        displayName: 'Custom Endpoint (BYO)',
        description: 'Bring your own OpenAI-compatible API endpoint',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'API Endpoint URL',
            description: 'The base URL for the chat completions API',
            tooltip: 'e.g., https://api.openai.com/v1 or your custom endpoint',
            constraints: { required: 'true' },
          },
        },
        openAIKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your API key for authentication',
            constraints: { required: 'true' },
          },
        },
      },
    },
  ],
  AgentLoop: [
    {
      name: 'AzureOpenAI_ManagedIdentity',
      uiDefinition: {
        displayName: 'Azure OpenAI (Managed Identity)',
        description: 'Connect to Azure OpenAI using Managed Identity authentication',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            tooltip: 'e.g., https://your-resource.openai.azure.com',
            constraints: { required: 'true' },
          },
        },
      },
    },
    {
      name: 'AzureOpenAI_ApiKey',
      uiDefinition: {
        displayName: 'Azure OpenAI (API Key)',
        description: 'Connect to Azure OpenAI using an API key',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            tooltip: 'e.g., https://your-resource.openai.azure.com',
            constraints: { required: 'true' },
          },
        },
        openAIKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your Azure OpenAI API key',
            constraints: { required: 'true' },
          },
        },
      },
    },
    {
      name: 'V1ChatCompletions',
      uiDefinition: {
        displayName: 'Custom Endpoint (BYO)',
        description: 'Bring your own OpenAI-compatible API endpoint',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        openAIEndpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'API Endpoint URL',
            description: 'The base URL for the chat completions API',
            tooltip: 'e.g., https://api.openai.com/v1 or your custom endpoint',
            constraints: { required: 'true' },
          },
        },
        openAIKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your API key for authentication',
            constraints: { required: 'true' },
          },
        },
      },
    },
  ],
  AzureOpenAI: [
    {
      name: 'ManagedIdentity',
      uiDefinition: {
        displayName: 'Managed Identity (Recommended)',
        description: 'Use Azure Managed Identity for secure authentication',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        endpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            tooltip: 'e.g., https://your-resource.openai.azure.com',
            constraints: { required: 'true' },
          },
        },
      },
    },
    {
      name: 'ApiKey',
      uiDefinition: {
        displayName: 'API Key',
        description: 'Use an API key for authentication',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        endpoint: {
          type: 'string',
          uiDefinition: {
            displayName: 'Azure OpenAI Endpoint',
            description: 'The endpoint URL of your Azure OpenAI resource',
            constraints: { required: 'true' },
          },
        },
        apiKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your Azure OpenAI API key',
            constraints: { required: 'true' },
          },
        },
      },
    },
  ],
  OpenAI: [
    {
      name: 'ApiKey',
      uiDefinition: {
        displayName: 'API Key Authentication',
        description: 'Use your OpenAI API key',
      },
      parameters: {
        displayName: {
          type: 'string',
          uiDefinition: {
            displayName: 'Connection Name',
            description: 'A friendly name for this connection',
            constraints: { required: 'true' },
          },
        },
        apiKey: {
          type: 'securestring',
          uiDefinition: {
            displayName: 'API Key',
            description: 'Your OpenAI API key (starts with sk-)',
            constraints: { required: 'true' },
          },
        },
        organization: {
          type: 'string',
          uiDefinition: {
            displayName: 'Organization ID',
            description: 'Optional organization ID',
            constraints: { required: 'false' },
          },
        },
      },
    },
  ],
};
